import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthStore } from '../store/auth.store';
import { CompanyContextStore } from '../store/company-context.store';
import { SessionService } from '../services/session.service';
import { TokenRefreshService } from '../services/token-refresh.service';
import { environment } from '../../../environments/environment';

/**
 * Rutas del servicio de autenticación (login, refresh, recuperación). No llevan
 * token y su 401 es una credencial incorrecta, nunca una sesión vencida.
 */
const AUTH_URL_PREFIX = `${environment.apiUrl}/auth/`;

const NO_TENANT_ENDPOINTS = [
  `${environment.apiUrl}/api/v1/users/me`,
];

const NO_ROLE_ERROR_CODES = ['no_role_assigned', 'user_has_no_role', 'role_not_found'];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore  = inject(AuthStore);
  const companyCtx = inject(CompanyContextStore);
  const tokens     = inject(TokenRefreshService);
  const session    = inject(SessionService);

  if (req.url.startsWith(AUTH_URL_PREFIX) || !authStore.idToken()) {
    return next(req);
  }

  const authorize = (request: HttpRequest<unknown>, token: string): HttpRequest<unknown> => {
    let headers = request.headers.set('Authorization', `Bearer ${token}`);

    const tenantId = companyCtx.companyId();
    const omitTenant = NO_TENANT_ENDPOINTS.some((url) => request.url.startsWith(url));
    if (tenantId && !omitTenant) {
      headers = headers.set('X-Tenant-ID', tenantId);
    }
    return request.clone({ headers });
  };

  return tokens.getValidToken().pipe(
    switchMap((token) => next(token ? authorize(req, token) : req)),
    catchError((error: HttpErrorResponse) => {
      const code: string = error.error?.error?.code ?? '';

      if (error.status === 403 && NO_ROLE_ERROR_CODES.includes(code)) {
        session.end('no_role');
        return throwError(() => error);
      }

      if (error.status !== 401) {
        return throwError(() => error);
      }

      // Sesión vencida: se renueva y se reintenta el request original una vez,
      // de modo que el usuario no pierda la acción que estaba ejecutando.
      return tokens.refresh().pipe(
        catchError(() => {
          session.end('session_expired');
          return throwError(() => error);
        }),
        switchMap((renewed) =>
          next(authorize(req, renewed)).pipe(
            catchError((retryError: HttpErrorResponse) => {
              if (retryError.status === 401) session.end('session_expired');
              return throwError(() => retryError);
            }),
          ),
        ),
      );
    }),
  );
};
